/**
 * HopDriveConfigSchema - Main config Zod schema
 *
 * AUTO-BUILT - DO NOT EDIT
 * Built from src/definitions/
 * Run `pnpm build:schemas` to rebuild
 */

import { z } from 'zod'
import { allDefinitions } from '../definitions/index.js'

/**
 * Complete HopDrive configuration schema.
 * All properties are optional (sparse configs merged hierarchically).
 */
// Explicit type annotation to avoid TS7056 (type too long to serialize)
export const HopDriveConfigSchema: z.ZodObject<z.ZodRawShape> = z.object({
  version: allDefinitions.version.schema.optional(),
  api_enabled: allDefinitions.api_enabled.schema.optional(),
  api_config: allDefinitions.api_config.schema.optional(),
  price_estimates: allDefinitions.price_estimates.schema.optional(),
  distance_estimates: allDefinitions.distance_estimates.schema.optional(),
  allow_email_notifications: allDefinitions.allow_email_notifications.schema.optional(),
  allow_triangle_moves: allDefinitions.allow_triangle_moves.schema.optional(),
  enable_csv_import: allDefinitions.enable_csv_import.schema.optional(),
  public_workflowsets: allDefinitions.public_workflowsets.schema.optional(),
  rideshare_calling_enabled: allDefinitions.rideshare_calling_enabled.schema.optional(),
  ride_provider_algorithm_key: allDefinitions.ride_provider_algorithm_key.schema.optional(),
  passthrough_rideshare_surges: allDefinitions.passthrough_rideshare_surges.schema.optional(),
  require_third_party_payer: allDefinitions.require_third_party_payer.schema.optional(),
  auto_assign_ref: allDefinitions.auto_assign_ref.schema.optional(),
  default_move_type: allDefinitions.default_move_type.schema.optional(),
  allowed_move_types: allDefinitions.allowed_move_types.schema.optional(),
  allowed_templates: allDefinitions.allowed_templates.schema.optional(),
  add_moves_always: allDefinitions.add_moves_always.schema.optional(),
  add_moves_disabled: allDefinitions.add_moves_disabled.schema.optional(),
  cancellation_fee: allDefinitions.cancellation_fee.schema.optional(),
  delay_price_per_minute: allDefinitions.delay_price_per_minute.schema.optional(),
  dry_run_behavior: allDefinitions.dry_run_behavior.schema.optional(),
  fuel_multiplier: allDefinitions.fuel_multiplier.schema.optional(),
  platform_charge_amount: allDefinitions.platform_charge_amount.schema.optional(),
  default_workflow_set_id: allDefinitions.default_workflow_set_id.schema.optional(),
  pickup_workflow_id: allDefinitions.pickup_workflow_id.schema.optional(),
  delivery_workflow_id: allDefinitions.delivery_workflow_id.schema.optional(),
  default_pickup_workflow_id: allDefinitions.default_pickup_workflow_id.schema.optional(),
  default_delivery_workflow_id: allDefinitions.default_delivery_workflow_id.schema.optional(),
  default_payer: allDefinitions.default_payer.schema.optional(),
  reference_num_label: allDefinitions.reference_num_label.schema.optional(),
  reference_num_required_for_pickup: allDefinitions.reference_num_required_for_pickup.schema.optional(),
  reference_num_required_for_return: allDefinitions.reference_num_required_for_return.schema.optional(),
  reference_num_report_enabled: allDefinitions.reference_num_report_enabled.schema.optional(),
  reference_num_report_label: allDefinitions.reference_num_report_label.schema.optional(),
  serviceable_radius_miles: allDefinitions.serviceable_radius_miles.schema.optional(),
  customer_serviceable_radius_miles: allDefinitions.customer_serviceable_radius_miles.schema.optional(),
  serviceable_radius_by_delivery: allDefinitions.serviceable_radius_by_delivery.schema.optional(),
  serviceable_radius_by_pickup: allDefinitions.serviceable_radius_by_pickup.schema.optional(),
  ride_type_override: allDefinitions.ride_type_override.schema.optional(),
  branding: allDefinitions.branding.schema.optional(),
  appraisals: allDefinitions.appraisals.schema.optional(),
  appointments: allDefinitions.appointments.schema.optional(),
  appointment_management: allDefinitions.appointment_management.schema.optional(),
  hopdrive_contacts: allDefinitions.hopdrive_contacts.schema.optional(),
  customer_values: allDefinitions.customer_values.schema.optional(),
  fuel: allDefinitions.fuel.schema.optional(),
  fraud_audit: allDefinitions.fraud_audit.schema.optional(),
  dms: allDefinitions.dms.schema.optional(),
  email_scrape_config: allDefinitions.email_scrape_config.schema.optional(),
  rc_config: allDefinitions.rc_config.schema.optional(),
  private: allDefinitions.private.schema.optional(),
  routing: allDefinitions.routing.schema.optional(),
  driver_pipeline: allDefinitions.driver_pipeline.schema.optional(),
  connector_triggers: allDefinitions.connector_triggers.schema.optional(),
  minimum_certification_ids: allDefinitions.minimum_certification_ids.schema.optional(),
  billing_frequency: allDefinitions.billing_frequency.schema.optional(),
  payment_terms: allDefinitions.payment_terms.schema.optional(),
  auto_pay: allDefinitions.auto_pay.schema.optional(),
  isShuttle: allDefinitions.isShuttle.schema.optional(),
  firstShuttleMove: allDefinitions.firstShuttleMove.schema.optional(),
  lastShuttleMove: allDefinitions.lastShuttleMove.schema.optional(),
  chase_id: allDefinitions.chase_id.schema.optional(),
  parent_chase_id: allDefinitions.parent_chase_id.schema.optional(),
  batch: allDefinitions.batch.schema.optional(),
  ride_orchestration: allDefinitions.ride_orchestration.schema.optional(),
  superDispatch: allDefinitions.superDispatch.schema.optional(),
  referenceNumbers: allDefinitions.referenceNumbers.schema.optional(),
  isGhost: allDefinitions.isGhost.schema.optional(),
  vin: allDefinitions.vin.schema.optional(),
  triangle_move: allDefinitions.triangle_move.schema.optional(),
  isFlex: allDefinitions.isFlex.schema.optional(),
  moveInput: allDefinitions.moveInput.schema.optional(),
}).describe(
  'Complete HopDrive configuration. All properties optional (sparse configs merged hierarchically).'
)

export type HopDriveConfig = z.infer<typeof HopDriveConfigSchema>

/**
 * Validate a HopDrive configuration object.
 * @param config - Unknown data to validate
 * @returns Validated HopDriveConfig
 * @throws ZodError if validation fails
 */
export function validateHopDriveConfig(config: unknown): HopDriveConfig {
  return HopDriveConfigSchema.parse(config)
}

/**
 * Safely validate a HopDrive configuration object.
 * @param config - Unknown data to validate
 * @returns Result object with success status and data/error
 */
export function safeValidateHopDriveConfig(config: unknown) {
  return HopDriveConfigSchema.safeParse(config)
}
